'use client';

import { Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { CommonCarousel } from '../../../Carousel';
import { ResultsSectionDecorations, ResultsSectionHeader, ResultsSectionSingleResult } from '../../../lotteries/ResultsSection/ResultsSection';
import type { LotteryName } from '../../../lottery-purchases/types';
import { useResultsCarouselData } from '../../hooks/useResultsCarouselData';

type ResultsCarouselProps = ComponentProps<'div'> & {
  lotteries: LotteryName[];
  namespace: string;
};

function ResultsCarousel({ lotteries, namespace, className, ...props }: ResultsCarouselProps) {
  const t = useTranslations(namespace);
  const { displayData, hasResults, isLoadingResults } = useResultsCarouselData(lotteries);

  if (!isLoadingResults && !hasResults) return null;

  const options = {
    basis: 'basis-79',
    disableHoverEffect: true,
  };

  const components = isLoadingResults
    ? {
        children: Array.from({ length: 6 }).map((_, index) => <Skeleton className="h-full w-75 shrink-0" key={index} />),
        title: <Skeleton className="h-8 w-80" />,
        options: { ...options, showControls: false },
      }
    : {
        children: displayData.map((result, index) => {
          return (
            <ResultsSectionDecorations
              key={index}
              lotteryName={result.lotteryId}
              className="h-full w-75 border-border border-1 "
              data-track-container="Result"
              data-track-position={index + 1}
              data-track-product={result.lotteryId}
            >
              <ResultsSectionHeader drawingDate={result.drawingDate} lotteryName={result.lotteryId} />
              <ResultsSectionSingleResult
                drawingId={result.drawingId}
                result={{ lotteryId: result.lotteryId, numbers: result.numbers, extraNumbers: result.extraNumbers, extraGames: result.extraGames }}
              />
            </ResultsSectionDecorations>
          );
        }),
        title: <h5 data-track-name="Title">{t('title')}</h5>,
        options,
      };

  return (
    <div className={twMerge('carousel-gap-offset', className)} {...props} data-testid="results-carousel">
      <CommonCarousel title={components.title} options={components.options} data-track-container="Carousel" data-track-carousel-position="3" data-track-carousel-title="Results">
        {components.children}
      </CommonCarousel>
    </div>
  );
}

export { ResultsCarousel, type ResultsCarouselProps };
